<template>
  <div class="jps-bullet-view">
    <ul>
      <li v-for="item in items" :key="item.key" :id="item.key">
        <UiButtonIcon
          tag="button"
          v-if="editstate"
          class="jps-icon-button list-delete"
          @click.stop="emits('delete-item', item)"
          icon="mdi/close-circle"
        />
        <article v-if="!item.readNext" class="media">
          <BaseLink
            class="media-left jps-relative"
            v-bind="baseLinkProps(item)"
            @click="emits('show-modal', item)"
            :show-icon="false"
          >
            <p class="image">
              <UiImageSelection
                :w="80"
                :h="80"
                :type="item.type"
                :image="item.image"
                :appearance="{
                  borderRadius: 'medium',
                }"
                :background="isJps ? '#c1c1c1' : 'white'"
                image-usage="thumbnail"
              />
              <ListEditIcon :item="item" field="image" :editstate="editstate" />
            </p>
          </BaseLink>
          <div class="media-content">
            <BaseLink
              v-if="ls(item.title)"
              class="bullet-title"
              v-bind="baseLinkProps(item)"
              @click="emits('show-modal', item)"
            >
              {{ ls(item.title) }}
              <ListEditIcon :item="item" field="title" :editstate="editstate" />
              <ListEditIcon :item="item" field="url" :editstate="editstate" />
              <ListEditIcon :item="item" field="linkUrl" :editstate="editstate" />
            </BaseLink>
            <p class="jps-bullet-basic" v-if="!isEmpty(item.contributor)">
              <span>{{ ljoin(item.contributor) }}</span>
            </p>
            <p class="jps-bullet-provider">
              <template v-if="item.type === 'item' && item.data">
                <UiOrgProviderSpan
                  v-if="isJps && item.data.common.provider"
                  :id="item.data.common.provider"
                />
                <UiDbSpan
                  :id="item.data.common.database"
                />
              </template>
              <template v-else-if="isJps && item.type !== 'curation'">
                <UiOrgSpan
                  v-if="item.ownerOrgId"
                  :id="item.ownerOrgId"
                />
              </template>
            </p>
            <p v-if="ls(item.description)" class="jps-bullet-description">
              {{ ls(item.description) }}
              <ListEditIcon
                :item="item"
                field="description"
                :editstate="editstate"
              >
              </ListEditIcon>
            </p>
            <template v-if="item.def">
              <p class="jps-bullet-note" v-if="editable">
                <LangDeltaEditor :lang-delta="item.def.note" :nolang="isNoLang" />
              </p>
              <p
                class="jps-bullet-note"
                v-else-if="!isEmptyLangDelta(item.def.note)"
              >
                <DeltaRenderer :delta="langDelta(item.def.note)" />
              </p>
            </template>
          </div>
          <div
            v-if="item.data && !editable"
            class="media-right"
          >
            <UiInteractPanel
              :type="item.type"
              :data="item.data"
              :image="item.image"
              class="jps-card-icon"
            />
          </div>
        </article>
        <SearchResultDiv v-else :item="item" :editstate="editstate" />
      </li>
      <li v-if="editstate && editstate.mode !== 'note'" class="add-button-li">
        <button
          class="jps-icon-button list-add-button jps-sub-orange"
          @click="emits('add')"
        >
          <BaseIcon name="mdi/plus-circle" :alt="ls(addPartMsg)" size="size-4" />
        </button>
      </li>
    </ul>
  </div>
</template>

<script lang="ts" setup>
const { ls, ljoin, langDelta } = useI18n()
const { isJps } = useServiceType()

interface Props {
  editstate?: CurationEditState
  def : PartList
  items: ListElement[]
}
const props = withDefaults(defineProps<Props>(), {})

const emits = defineEmits(['delete-item', 'show-modal', 'add'])

const {
  isNoLang,
  hasExternalLink,
  baseLinkProps,
  editable,
  addPartMsg,
} = useCurationListView(props.editstate, props.def)
</script>

<style lang="scss">
@use '@/assets/styles/scss/vue' as *;

.jps-bullet-note {
  .lang-delta-editor {
    .jps-quill-editor {
      .ql-editor {
        min-height: 1rem;
      }
    }
  }
}
.part-editor {
  .jps-bullet-view {
    ul {
      li {
        .jps-bullet-note {
          border: 1px solid $border-input !important;
          @include focus-within;
          &:focus-within {
            border-color: $jps-key-blue !important;
          }
        }
      }
    }
  }
}
</style>

<style lang="scss" scoped>
@use '@/assets/styles/scss/vue' as *;

.jps-bullet-view {
  ul {
    @include is-mobile {
      padding: 0 0.5rem;
    }
    li {
      position: relative;

      .list-delete {
        position: absolute;
        right: 0px;
        top: 0px;
        background: $jps-gray-50;
      }
      .media {
        @include is-mobile {
          display: grid;
          grid-template-columns: 96px 1fr;
        }
        .media-right {
          grid-column: span 2;
        }
        .image {
          width: 5rem;
          height: 5rem;
          // background: $jps-gray-300;
          cursor: pointer;
          // border-radius: $global-radius;
          overflow: hidden;
        }
        .media-content {
          min-height: 7rem;
          display: flex;
          flex-direction: column;
          justify-content: center;

          .bullet-title {
            font-weight: bolder;
          }
        }
      }

      .jps-bullet-basic {
        color: $jps-gray-600;
      }

      .jps-bullet-provider {
        color: $jps-gray-600;
      }

      .jps-bullet-kv {
        font-size: 0.75rem;
        .jps-bullet-kvh {
          margin-left: 1rem;
          font-weight: 600;
        }
      }
      .jps-bullet-description {
        padding-left: 1rem;
        margin: 0.25rem 0 0.5rem;
        font-size: 0.75rem;
        white-space: pre-line;
        .material-icons {
          font-size: 1rem;
        }
      }
      .jps-bullet-note {
        border: 1px solid $jps-gray-300;
        border-radius: $global-radius-small;
        font-size: 0.75rem;
        background: $white;
        .lang-delta-editor {
          .jps-quill-editor {
            .ql-editor {
              min-height: 1rem;
            }
          }
        }
        .delta-renderer {
          margin: 0.5rem;
        }
      }
      &:not(:last-child) {
        margin-bottom: 2rem;
      }
    }
    .jps-show-more {
      display: flex;
      justify-content: center;
      width: 100%;
      height: auto;
    }
    .add-button-li {
      text-align: center;
      padding: 1rem;
    }
  }
  // 画像編集アイコンの位置調整
  :deep(.edit-image-static) {
    position: absolute;
    right: 8px;
    bottom: 2px;
  }
}
</style>
